import Layout from 'components/layout';

import React, { FC } from 'react';

import { getI18nData } from '@lucid/utils';
import { graphql } from 'gatsby';
import { Box, Heading, LayoutContext, Container } from '@lucid/components-react';
import {
  Drupal_FieldNodeRolesHubFieldHeaderCta,
  Drupal_NodeRolesHub,
  Drupal_ParagraphCtaButton,
  Drupal_ParagraphFullCard,
  Drupal_ParagraphHeroSection,
  Drupal_ParagraphQuote
} from 'generated/drupal-graphql';
import { mqMax, LucidTheme } from '@lucid/theme';

import FooterCard from 'components/footer-card/footer-card';
import PaperHubList from 'components/paper-hub-list/paper-hub-list';
import Quote from 'components/quote/quote';
import SunriseHeader from 'components/sunrise-header/sunrise-header';
import Tiles from 'components/tiles/tiles';
import { backgroundGrayPattern } from 'theme/shared';
import { PageTemplateProps, isDefined } from 'types/types';
import { getEntityFields } from 'utils/getEntityFields';
import SignUpCta from 'components/sign-up-cta/sign-up-cta';

const RoleHub: FC<PageTemplateProps> = ({ data, pageContext, location }) => {
  const { drupal, i18nData } = data;
  const node = drupal.nodeById as Drupal_NodeRolesHub;
  const { entityMetatags, entityTranslations } = getEntityFields(node);
  const { langPrefix, pageType, nid, i18nKey, shouldHideSsr } = pageContext;

  const i18n = getI18nData(i18nData, i18nKey);

  const headerSection = node.fieldHeaderSection
    ? (node.fieldHeaderSection.entity as Drupal_ParagraphHeroSection)
    : null;

  const fullTitle =
    node.fieldTitlePiece && node.fieldTitlePiece.length > 0
      ? (node.fieldTitlePiece as string[])
      : (headerSection?.fieldHeader as string);

  const rolesHub = drupal.nodeQuery
    ? (drupal.nodeQuery.entities
        ?.filter((entity: any) => entity?.entityTranslation)
        .map((entity: any) => entity?.entityTranslation) as Drupal_NodeRolesHub[])
    : null;

  const tiles = node
    .fieldHowToSection!.filter(isDefined)
    .map(({ entity }) => entity as Drupal_ParagraphFullCard);

  const headerCtas = (node.fieldHeaderCta as Drupal_FieldNodeRolesHubFieldHeaderCta[]).map(
    ({ entity }) => entity as Drupal_ParagraphCtaButton
  );

  const quote = node.fieldQuoteBundle
    ? (node.fieldQuoteBundle.entity as Drupal_ParagraphQuote)
    : null;

  return (
    <Layout
      entityTranslations={entityTranslations}
      langPrefix={langPrefix}
      location={location}
      metatags={entityMetatags}
      nid={nid}
      pageType={pageType}
      shouldHideSsr={shouldHideSsr}
    >
      <LayoutContext.Consumer>
        {() => (
          <>
            <Box
              as="main"
              css={() => [
                backgroundGrayPattern
              ]}
            >
              {headerSection && (
                <SunriseHeader
                  hasBackgroundImage
                  backgroundImage={headerSection.fieldImage!}
                  breadCrumbHub={i18n('mktg_footer_solutions')}
                  breadCrumbPage={node.title!}
                  buttons={headerCtas}
                  header={fullTitle}
                  pageType="hub"
                  subheader={headerSection.fieldSubheader!}
                />
              )}
              {rolesHub && <PaperHubList withText paper={rolesHub} />}
              {tiles && <Tiles tiles={tiles} />}
              {quote && <Quote quote={quote} />}
              <Box
                css={(theme: LucidTheme) => ({
                  paddingBottom: theme.space[10],
                  paddingTop: theme.space[10]
                })}
              >
                <Container>
                  <FooterCard>
                    <Box
                      css={{
                        display: 'flex',
                        [mqMax.mobile]: { flexDirection: 'column' }
                      }}
                    >
                      <Heading
                        as="h2"
                        css={(theme: LucidTheme) => ({
                          flex: '1',
                          margin: `0 ${theme.space[9]}px 0 0`,
                          [mqMax.mobile]: { margin: `0 0 ${theme.space[5]}px 0` }
                        })}
                        variant="headlineXL"
                      >
                        {i18n('mktg_ct_consideration_footer_header')}
                      </Heading>
                      <Box
                        css={{ margin: 'auto', [mqMax.mobile]: { width: '100%' } }}
                      >
                        <SignUpCta
                          css={{ [mqMax.mobile]: { width: '100%' } }}
                        >
                          {i18n('mktg-nav-sign-up-free')}
                        </SignUpCta>
                      </Box>
                    </Box>
                  </FooterCard>
                </Container>
              </Box>
            </Box>
          </>
        )}
      </LayoutContext.Consumer>
    </Layout>
  );
};

export default RoleHub;

export const query = graphql`
  query(
    $nid: String!
    $LanguageId: Drupal_LanguageId!
    $i18nKey: String!
    $persona: [String!]
  ) {
    drupal {
      nodeById(id: $nid, language: $LanguageId) {
        ... on Drupal_NodeRolesHub {
          ...entityFields
          title
          fieldHeaderSection {
            entity {
              ... on Drupal_ParagraphHeroSection {
                fieldHeader
                fieldSubheader
                fieldImage
              }
            }
          }
          fieldTitlePiece
          fieldHeaderCta {
            entity {
              ... on Drupal_ParagraphCtaButton {
                fieldButtonLink
                fieldButtonText
                fieldIsSecondary
                fieldMarketo {
                  entity {
                    ... on Drupal_ParagraphMarketoForm {
                      fieldMarketoId
                    }
                  }
                }
              }
            }
          }
          fieldHowToSection {
            entity {
              ... on Drupal_ParagraphFullCard {
                fieldImage
                fieldCtaHeading
                fieldLink
                fieldCardSubheader {
                  processed
                }
              }
            }
          }
          fieldQuoteBundle {
            entity {
              ... on Drupal_ParagraphQuote {
                fieldQuote
                fieldAttribution
              }
            }
          }
        }
      }
      nodeQuery(
        limit: 1000
        filter: {
          conditions: [
            { field: "type", value: ["individual_role"], operator: IN }
            { field: "status", value: "1", operator: EQUAL }
            { field: "field_role_type", value: $persona, operator: EQUAL }
          ]
        }
      ) {
        entities {
          entityTranslation(language: $LanguageId) {
            ... on Drupal_NodeIndividualRole {
              entityUrl {
                path
              }
              fieldRolesHubContent {
                entity {
                  entityTranslation(language: $LanguageId) {
                    ... on Drupal_ParagraphPaper {
                      fieldTitle
                      fieldSubtitle
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
    i18nData: allFile(
      filter: {
        sourceInstanceName: { eq: "i18n" }
        name: { in: [$i18nKey, "en"] }
      }
    ) {
      edges {
        node {
          name
          childSrcJson {
            mktg_ct_all_request_a_demo
            mktg_ct_all_get_a_quote
            mktg_ct_consideration_footer_header
            mktg_footer_solutions
          }
        }
      }
    }
  }
`;
